import {
  StreamCollection,
  getStreamCollectionInitialState,
  toStreamCollection,
} from 'streams/utils/streamUtils';
import { APIModelAttributes, ResourceType } from '@ardoq/api-types';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { toIdSet } from '@ardoq/core';
import { distinctUntilChanged, map } from 'rxjs';
import {
  reducedStream,
  reducer,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import * as modelActions from './actions';
import { fetchAllReducer, websocketReducer } from 'streams/crud/reducers';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { removeEntityById, updateEntity } from '@ardoq/common-helpers';

export type ModelsState = StreamCollection<APIModelAttributes>;

const resetModels = (
  _: APIModelAttributes[],
  models: APIModelAttributes[]
): APIModelAttributes[] => models;

const clearModels = (_: APIModelAttributes[]): APIModelAttributes[] => [];

const addModels = (
  state: APIModelAttributes[],
  models: APIModelAttributes[]
): APIModelAttributes[] => {
  const existingIds = toIdSet(state);
  const newModels = models.filter(m => !existingIds.has(m._id));
  return [...state, ...newModels];
};

const createModel = (
  state: APIModelAttributes[],
  model: APIModelAttributes
): APIModelAttributes[] => {
  if (state.some(m => m._id === model._id)) {
    return state;
  }
  return [...state, model];
};

// Replace-or-insert the synthetic virtual model without touching the real org
// models; null removes it (no collapsed paths in scope).
const setVirtualModel = (
  state: APIModelAttributes[],
  model: APIModelAttributes | null
): APIModelAttributes[] => {
  const withoutVirtual = state.filter(m => m._id !== VIRTUAL_MODEL_ID);
  return model ? [...withoutVirtual, model] : withoutVirtual;
};

const _modelsAndTemplates$ = reducedStream(
  '_modelAndTemplates',
  [],
  [
    fetchAllReducer(resetModels),
    reducer(modelActions.resetModels, clearModels),
    reducer(modelActions.resetModelsWithData, resetModels),
    reducer(modelActions.addModels, addModels),
    reducer(modelActions.updateModel, updateEntity<APIModelAttributes>),
    reducer(modelActions.setVirtualModel, setVirtualModel),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIModelAttributes>(ResourceType.MODEL)
      ),
      websocketReducer({
        create: createModel,
        update: updateEntity,
        delete: removeEntityById,
      })
    ),
  ],
  {
    namespace: modelActions.modelNamespace,
  }
);

const modelsAndTemplates$ = toPersistentStream(
  'modelsAndTemplates$',
  _modelsAndTemplates$.pipe(distinctUntilChanged(), map(toStreamCollection)),
  getStreamCollectionInitialState()
);

const onlyTemplates = ({
  models,
}: {
  models: APIModelAttributes[];
}): StreamCollection<APIModelAttributes> =>
  toStreamCollection(models.filter(m => m.useAsTemplate));

const onlyModels = ({
  models,
}: {
  models: APIModelAttributes[];
}): StreamCollection<APIModelAttributes> =>
  toStreamCollection(models.filter(m => !m.useAsTemplate));

export const templates$ = modelsAndTemplates$.pipe(map(onlyTemplates));

export const models$ = toPersistentStream(
  'models$',
  modelsAndTemplates$.pipe(map(onlyModels)),
  getStreamCollectionInitialState()
);
